/**
 * Node.js HTTP Client adapter for PERS SDK
 *
 * Simple HTTP client for Node.js environments using native fetch (Node 18+) or a polyfill.
 * Works with Express, Next.js API routes, or any Node.js-based backend.
 *
 * @example
 * ```typescript
 * import { createPersSDK } from '@explorins/pers-sdk';
 * import { NodeHttpClientAdapter } from '@explorins/pers-sdk/platform-adapters';
 *
 * const sdk = createPersSDK(
 *   new NodeHttpClientAdapter(),
 *   { environment: 'production', apiProjectKey: 'your-key' }
 * );
 *
 * // Use managers directly
 * await sdk.user.getCurrentUser();
 * ```
 */
class NodeHttpClientAdapter {
    async get(url, options = {}) {
        return this.request(url, 'GET', undefined, options);
    }
    async post(url, data = null, options = {}) {
        return this.request(url, 'POST', data, options);
    }
    async put(url, data = null, options = {}) {
        return this.request(url, 'PUT', data, options);
    }
    async delete(url, options = {}) {
        return this.request(url, 'DELETE', undefined, options);
    }
    async patch(url, data = null, options = {}) {
        return this.request(url, 'PATCH', data, options);
    }
    async request(url, method, data, options = {}) {
        // Use native fetch in Node 18+ or require a polyfill for older versions
        const fetchImpl = globalThis.fetch || require('node-fetch');
        const headers = {
            'Content-Type': 'application/json',
            'User-Agent': 'PERS-SDK-Node/1.0',
            ...options.headers
        };
        const fetchOptions = {
            method,
            headers,
            ...options
        };
        if (data !== undefined && method !== 'GET') {
            fetchOptions.body = JSON.stringify(data);
        }
        const response = await fetchImpl(url, fetchOptions);
        if (!response.ok) {
            // Extract backend error response if available
            let errorData;
            try {
                const contentType = response.headers.get('content-type');
                if (contentType?.includes('application/json')) {
                    errorData = await response.json();
                }
            }
            catch {
                // If parsing fails, continue with basic error
            }
            // Create error with backend data attached
            const error = new Error(errorData?.message || `HTTP ${response.status}: ${response.statusText}`);
            error.status = response.status;
            error.statusText = response.statusText;
            // Attach backend error response for extraction
            if (errorData) {
                Object.assign(error, errorData);
            }
            throw error;
        }
        // Handle different response types
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
            return response.json();
        }
        return response.text();
    }
}

export { NodeHttpClientAdapter as N };
//# sourceMappingURL=node-http-client-DloDLfm9.js.map
